/**
 * Browser-side image preparation (decode → rotate per EXIF → scale → encode), entirely local.
 * The storage policy lives in domain/attachments.ts; this module only moves pixels.
 */
import { IMAGE_LIMITS, planImageProcessing, UnsupportedImageError, validateImageFile } from "@/domain/attachments";

export interface ProcessedImage {
  data: ArrayBuffer;
  mimeType: string;
  width: number;
  height: number;
  processing: "keep" | "resize" | "reencode";
  original: { name: string | null; type: string; byteSize: number; width: number; height: number; lastModified: number | null };
}

/** Injected into the data layer so storage logic can be tested without a canvas. */
export type ImageProcessor = (file: File) => Promise<ProcessedImage>;

async function encode(bitmap: ImageBitmap, width: number, height: number, type: string): Promise<ArrayBuffer> {
  const draw = (ctx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D) => {
    // JPEG has no transparency: paint white first so transparent screenshots stay readable.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bitmap, 0, 0, width, height);
  };
  if (typeof OffscreenCanvas !== "undefined") {
    const canvas = new OffscreenCanvas(width, height);
    draw(canvas.getContext("2d")!);
    return (await canvas.convertToBlob({ type, quality: IMAGE_LIMITS.outputQuality })).arrayBuffer();
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  draw(canvas.getContext("2d")!);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, IMAGE_LIMITS.outputQuality));
  if (!blob) throw new UnsupportedImageError("Impossibile elaborare l'immagine su questo dispositivo.");
  return blob.arrayBuffer();
}

export const processImage: ImageProcessor = async (file) => {
  const type = validateImageFile(file);
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    throw new UnsupportedImageError("Questa immagine non può essere letta dal browser (formato non supportato o file danneggiato).");
  }
  try {
    const plan = planImageProcessing({ type, size: file.size, width: bitmap.width, height: bitmap.height });
    const data = plan.action === "keep" ? await file.arrayBuffer() : await encode(bitmap, plan.width, plan.height, plan.outputType);
    return {
      data,
      mimeType: plan.outputType,
      width: plan.width,
      height: plan.height,
      processing: plan.action,
      original: { name: file.name || null, type: file.type || type, byteSize: file.size, width: bitmap.width, height: bitmap.height, lastModified: file.lastModified || null },
    };
  } finally {
    bitmap.close();
  }
};
